/**
 * A credential in the address bar, kept out of the session recorders.
 *
 * Five routes carry a bearer token in the path: the emailed sign-in link (`/sign-in/{token}`),
 * an invitation (`/register/{code}`), a password reset (`/reset/{token}`), the two-factor
 * challenge (`/login/{token}`) and a guest share (`/guest/{code}`). LogRocket records the page
 * URL as it initialises and on every navigation, and Sentry Replay keeps the initial URL, the
 * navigation breadcrumbs and the navigation timing entry in the buffer it uploads when an error
 * occurs. Redacting request bodies (`replay-privacy.js`) does nothing about any of that: the
 * token is still valid when the page loads and would be exported as it stands.
 *
 * So the token leaves the URL before either recorder starts. `hideLinkToken()` runs from the
 * first import of the entry point: it moves the token into per-tab storage and rewrites the
 * address to a fixed placeholder segment, and `RouteService.getRouteParam()` hands the stored
 * token back to the page that asks for it. Only that page: the token is stored with the route
 * it came from and the name of the parameter that carried it, and it is handed back only for
 * that parameter on that route -- an unrelated screen whose `q` happens to be the placeholder
 * word gets the word. It is forgotten when the router leaves the credential route, so it does
 * not outlive its use in the tab; a reload of the same route still finds it.
 *
 * `scrubUrl()` is the belt to that: for anything that captured the URL before the rewrite,
 * such as the navigation timing entry, both recorders are also told to rewrite what they
 * record. It covers one API path as well -- `/approval-actions/{token}`, an emailed one-shot
 * the client GETs -- since a request URL is recorded like any other.
 */

/**
 * What stands in for the token in the address bar. A word no token can be: tokens are UUIDs
 * or hex ids, and a legacy invitation code is base64 of an email address.
 */
export const PLACEHOLDER = 'link';

/**
 * The SPA routes that carry a credential, by their first path segment, and the name of the
 * route parameter that carries it (`modules/navigation/states.config.js`).
 */
export const CREDENTIAL_ROUTES = {
	'sign-in': 'token',
	register: 'code',
	reset: 'token',
	login: 'token',
	guest: 'code'
};

const ROUTE_NAMES = Object.keys(CREDENTIAL_ROUTES).join('|');

/**
 * The credential routes in a path, and the segment after them. Anchored to the start of the
 * path, since these are top-level routes.
 */
const ROUTE_SEGMENT = new RegExp(`^/(${ROUTE_NAMES})/([^/?#]+)`);

/**
 * Everything `scrubUrl()` rewrites, in any URL -- absolute or path-only: the SPA routes, and
 * the API's emailed approval action. One match per URL is enough, since a path has one route.
 */
const SCRUB_SEGMENT = new RegExp(`(/(?:${ROUTE_NAMES}|approval-actions)/)([^/?#]+)`);

const STORAGE_KEY = 'pixwel.link-token';

// Where the record lives when the tab's storage is unavailable. Survives the page's lifetime,
// which is all a token spent by the page needs.
let inMemory = null;

const store = (record) => {
	inMemory = record;

	try {
		window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
	}
	catch{
		// Private mode or blocked storage: the copy in memory serves this page load.
	}
};

const stored = () => {
	try {
		const raw = window.sessionStorage.getItem(STORAGE_KEY);

		if(raw) {
			const record = JSON.parse(raw);

			if(record && typeof record.token === 'string') {
				return record;
			}
		}
	}
	catch{
		// Fall through to memory.
	}

	return inMemory;
};

/**
 * The first path segment of a pathname, when it is a credential route.
 *
 * @param {string} pathname
 * @returns {string|null}
 */
export const credentialRouteOf = (pathname) => {
	const match = String(pathname || '').match(ROUTE_SEGMENT);

	return match ? match[1] : null;
};

/**
 * Whether a ui-router state URL pattern (`/sign-in/{token:string}`) is one of the credential
 * routes.
 *
 * @param {string} statePattern
 * @returns {boolean}
 */
export const isCredentialStateUrl = (statePattern) => credentialRouteOf(statePattern) !== null;

/**
 * The same URL with the credential segment replaced by the placeholder.
 *
 * @param {string} url
 * @returns {string}
 */
export const scrubUrl = (url) => {
	if(typeof url !== 'string') {
		return url;
	}

	return url.replace(SCRUB_SEGMENT, (match, route, token) => (
		token === PLACEHOLDER ? match : `${route}${PLACEHOLDER}`
	));
};

/**
 * Move the token out of the address bar and into storage, if there is one there.
 *
 * Idempotent: a URL already carrying the placeholder is left alone, and the stored token is
 * kept for the page to read.
 *
 * @param {Window} [win]
 * @returns {boolean} Whether a token was hidden.
 */
export const hideLinkToken = (win = window) => {
	const { location, history } = win;
	const match = String(location.pathname || '').match(ROUTE_SEGMENT);

	if(!match || match[2] === PLACEHOLDER) {
		return false;
	}

	const route = match[1];
	let token = null;

	try {
		token = decodeURIComponent(match[2]);
	}
	catch{
		// A malformed percent escape -- `/sign-in/%E0%A4%A` -- is not a token anybody was sent.
		// This runs at bootstrap, so throwing here would be a blank page; instead nothing is
		// stored, the segment still leaves the address bar, and the page is told the link is
		// not valid, which is the truth.
	}

	if(token) {
		store({ route, param: CREDENTIAL_ROUTES[route], token });
	}
	else {
		forgetLinkToken();
	}

	const scrubbed = scrubUrl(location.pathname) + (location.search || '') + (location.hash || '');

	try {
		history.replaceState(history.state, '', scrubbed);
	}
	catch{
		// A history that refuses (a sandboxed frame) leaves the URL as it was; the token is
		// still in storage, and the route parameter still resolves.
		return false;
	}

	return true;
};

/**
 * The value a route parameter stands for.
 *
 * The stored token, when the value is the placeholder, the page is on the route the token was
 * hidden from and the parameter is the one that carried it; the value itself in every other
 * case -- including an unrelated parameter that happens to be the placeholder word.
 *
 * @param {*} value
 * @param {Object} context
 * @param {string} context.param The route parameter's name.
 * @param {string} context.pathname The current path.
 * @returns {*}
 */
export const revealLinkToken = (value, { param, pathname } = {}) => {
	if(value !== PLACEHOLDER) {
		return value;
	}

	const record = stored();
	const route = credentialRouteOf(pathname);

	if(!record || !route || record.route !== route || record.param !== param) {
		return value;
	}

	return record.token;
};

/**
 * Forget the token: on request, and when the router leaves the credential route.
 */
export const forgetLinkToken = () => {
	inMemory = null;

	try {
		window.sessionStorage.removeItem(STORAGE_KEY);
	}
	catch{
		// Nothing to remove.
	}
};

/**
 * Scrub every field of a Sentry Replay custom event that can carry a URL: the navigation
 * breadcrumb's `from`/`to`, a performance span's `description` and `name`, a fetch or XHR
 * breadcrumb's `url`.
 *
 * @param {Object} event
 * @returns {Object}
 */
export const scrubReplayEvent = (event) => {
	const payload = event?.data?.payload;

	if(!payload || typeof payload !== 'object') {
		return event;
	}

	['description', 'name'].forEach((field) => {
		if(typeof payload[field] === 'string') {
			payload[field] = scrubUrl(payload[field]);
		}
	});

	if(payload.data && typeof payload.data === 'object') {
		['from', 'to', 'url'].forEach((field) => {
			if(typeof payload.data[field] === 'string') {
				payload.data[field] = scrubUrl(payload.data[field]);
			}
		});
	}

	return event;
};

/**
 * Scrub the URLs a Sentry error event carries: the request URL and any breadcrumb that names one.
 *
 * @param {Object} event
 * @returns {Object}
 */
export const scrubSentryEvent = (event) => {
	if(!event || typeof event !== 'object') {
		return event;
	}

	if(typeof event.request?.url === 'string') {
		event.request.url = scrubUrl(event.request.url);
	}

	(event.breadcrumbs || []).forEach((crumb) => scrubBreadcrumb(crumb));

	return event;
};

/**
 * Scrub a Sentry breadcrumb in place: navigation `from`/`to`, and a request's `url`.
 *
 * @param {Object} crumb
 * @returns {Object}
 */
export const scrubBreadcrumb = (crumb) => {
	if(!crumb?.data || typeof crumb.data !== 'object') {
		return crumb;
	}

	['from', 'to', 'url'].forEach((field) => {
		if(typeof crumb.data[field] === 'string') {
			crumb.data[field] = scrubUrl(crumb.data[field]);
		}
	});

	return crumb;
};
